import React, { useMemo } from "react"
import { WingsStoryCard } from "@wingspans-ui"
import { useIntl } from "react-intl"
import { JsonParam, useQueryParam } from "use-query-params"
import { useAllStories } from "../../data/graphql"
import {
  localizeEducationCheckboxTranslations,
  localizeIdentityCheckboxTranslation,
  localizeInterestCheckboxTranslation,
  localizePersonalityCheckboxTranslation,
} from "../algolia/localized-algolia-checkboxes"
import ExploreUnifiedFiltersDropdown from "./ExploreUnifiedFiltersDropdown"
import LocalExplore from "./LocalExplore"

const VIDEOS_PER_PAGE = 12

const FILTER_KEYS = {
  personality: "story_personality_filter",
  interests: "story_interests_filter",
  identity: "story_identity_filter",
  education: "story_required_education_filter",
}

const REFINEMENT_FIELDS = [
  FILTER_KEYS.personality,
  FILTER_KEYS.interests,
  FILTER_KEYS.identity,
  FILTER_KEYS.education,
]
const FILTER_ID_SEPARATOR = "::"
const ENABLE_EXCLUSIVE_OPTION_HIDING = false

const videosSearchKeys = [
  { name: "title", weight: 0.45 },
  { name: "story_job_title", weight: 0.3 },
  { name: "story_company", weight: 0.15 },
  { name: "story_location", weight: 0.1 },
]

const normalizeRefinementList = (refinementList) => {
  const normalized = {
    [FILTER_KEYS.personality]: [],
    [FILTER_KEYS.interests]: [],
    [FILTER_KEYS.identity]: [],
    [FILTER_KEYS.education]: [],
  }

  if (!refinementList || typeof refinementList !== "object") {
    return normalized
  }

  Object.keys(normalized).forEach((key) => {
    const values = refinementList[key]
    if (Array.isArray(values)) {
      normalized[key] = values
      return
    }

    normalized[key] = typeof values === "string" && values ? [values] : []
  })

  return normalized
}

const getLocalizedLabel = (value, translationMap, locale) => {
  return translationMap?.[value]?.[locale] || value
}

const matchesSelection = (values, selectedValues) => {
  if (!selectedValues.length) {
    return true
  }

  return (values || []).some((value) => selectedValues.includes(value))
}

const videoMatchesRefinements = (video, refinementList) => {
  return matchesSelection(video[FILTER_KEYS.personality], refinementList[FILTER_KEYS.personality])
    && matchesSelection(video[FILTER_KEYS.interests], refinementList[FILTER_KEYS.interests])
    && matchesSelection(video[FILTER_KEYS.identity], refinementList[FILTER_KEYS.identity])
    && matchesSelection(video[FILTER_KEYS.education], refinementList[FILTER_KEYS.education])
}

const getFilterCounts = (items, field, translationMap, locale) => {
  const counts = new Map()

  items.forEach((item) => {
    ;(item[field] || []).forEach((value) => {
      counts.set(value, (counts.get(value) || 0) + 1)
    })
  })

  return Array.from(counts.entries())
    .map(([value, count]) => ({
      value,
      label: getLocalizedLabel(value, translationMap, locale),
      count,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

const ensureSelectedOptionsVisible = (options, selectedValues, translationMap, locale) => {
  const optionMap = new Map(options.map((option) => [option.value, option]))

  selectedValues.forEach((value) => {
    if (!optionMap.has(value)) {
      optionMap.set(value, {
        value,
        label: getLocalizedLabel(value, translationMap, locale),
        count: 0,
      })
    }
  })

  return Array.from(optionMap.values()).sort((a, b) => a.label.localeCompare(b.label))
}

const mapVideoToCard = (video) => {
  return (
    <WingsStoryCard
      objectID={video.objectID || video.id}
      img={video.story_personal_photo}
      title={video.title}
      slug={video.slug}
      video={video.story_video_url}
      storyJobTitle={video.story_job_title}
      storyCompany={video.story_company}
      storyQuote={video.story_quote}
      contentType={video.type}
    />
  )
}

const VideosExplore = ({ classContext = "stories", filterList }) => {
  const { locale, formatMessage } = useIntl()
  const [queryFilters, setQueryFilters] = useQueryParam("queryFilters", JsonParam)
  const stories = useAllStories(locale)

  const videos = stories.filter((story) => Boolean(story.story_video_url))

  const selectedRefinements = useMemo(() => {
    return normalizeRefinementList(queryFilters?.refinementList)
  }, [queryFilters])

  const getVideosMatchingOtherRefinements = (excludedField) => {
    const refinementListWithoutField = REFINEMENT_FIELDS.reduce((acc, field) => {
      acc[field] = field === excludedField ? [] : (selectedRefinements[field] || [])
      return acc
    }, {})

    return videos.filter((video) => videoMatchesRefinements(video, refinementListWithoutField))
  }

  const personalityOptions = useMemo(() => {
    const matchingVideos = ENABLE_EXCLUSIVE_OPTION_HIDING
      ? getVideosMatchingOtherRefinements(FILTER_KEYS.personality)
      : videos
    const options = getFilterCounts(matchingVideos, FILTER_KEYS.personality, localizePersonalityCheckboxTranslation, locale)

    return ensureSelectedOptionsVisible(
      options,
      selectedRefinements[FILTER_KEYS.personality],
      localizePersonalityCheckboxTranslation,
      locale
    )
  }, [videos, selectedRefinements, locale])

  const interestsOptions = useMemo(() => {
    const matchingVideos = ENABLE_EXCLUSIVE_OPTION_HIDING
      ? getVideosMatchingOtherRefinements(FILTER_KEYS.interests)
      : videos
    const options = getFilterCounts(matchingVideos, FILTER_KEYS.interests, localizeInterestCheckboxTranslation, locale)

    return ensureSelectedOptionsVisible(
      options,
      selectedRefinements[FILTER_KEYS.interests],
      localizeInterestCheckboxTranslation,
      locale
    )
  }, [videos, selectedRefinements, locale])

  const identityOptions = useMemo(() => {
    const matchingVideos = ENABLE_EXCLUSIVE_OPTION_HIDING
      ? getVideosMatchingOtherRefinements(FILTER_KEYS.identity)
      : videos
    const options = getFilterCounts(matchingVideos, FILTER_KEYS.identity, localizeIdentityCheckboxTranslation, locale)

    return ensureSelectedOptionsVisible(
      options,
      selectedRefinements[FILTER_KEYS.identity],
      localizeIdentityCheckboxTranslation,
      locale
    )
  }, [videos, selectedRefinements, locale])

  const educationOptions = useMemo(() => {
    const matchingVideos = ENABLE_EXCLUSIVE_OPTION_HIDING
      ? getVideosMatchingOtherRefinements(FILTER_KEYS.education)
      : videos
    const options = getFilterCounts(matchingVideos, FILTER_KEYS.education, localizeEducationCheckboxTranslations, locale)

    return ensureSelectedOptionsVisible(
      options,
      selectedRefinements[FILTER_KEYS.education],
      localizeEducationCheckboxTranslations,
      locale
    )
  }, [videos, selectedRefinements, locale])

  const filteredVideos = useMemo(() => {
    return videos.filter((video) => videoMatchesRefinements(video, selectedRefinements))
  }, [videos, selectedRefinements])

  const toggleRefinement = (filterKey, value, isChecked) => {
    const currentRefinements = normalizeRefinementList(queryFilters?.refinementList)
    const currentValues = currentRefinements[filterKey] || []
    const nextValues = isChecked
      ? [...new Set([...currentValues, value])]
      : currentValues.filter((item) => item !== value)

    setQueryFilters({
      ...(queryFilters || {}),
      page: 1,
      refinementList: {
        ...currentRefinements,
        [filterKey]: nextValues,
      },
    })
  }

  const clearRefinements = () => {
    setQueryFilters({
      ...(queryFilters || {}),
      page: 1,
      refinementList: {},
    })
  }

  const selectedFilterOptionIds = useMemo(() => {
    return Object.entries(selectedRefinements).flatMap(([filterKey, values]) => {
      return (values || []).map((value) => `${filterKey}${FILTER_ID_SEPARATOR}${value}`)
    })
  }, [selectedRefinements])

  const filterSections = useMemo(() => {
    return [
      {
        id: FILTER_KEYS.personality,
        title: formatMessage({ id: "filter-by-personality", defaultMessage: "Personality" }),
        columns: 3,
        options: personalityOptions.map((option) => ({
          id: `${FILTER_KEYS.personality}${FILTER_ID_SEPARATOR}${option.value}`,
          label: option.label,
        })),
      },
      {
        id: FILTER_KEYS.interests,
        title: formatMessage({ id: "filter-by-interests", defaultMessage: "Interests" }),
        columns: 3,
        options: interestsOptions.map((option) => ({
          id: `${FILTER_KEYS.interests}${FILTER_ID_SEPARATOR}${option.value}`,
          label: option.label,
        })),
      },
      {
        id: FILTER_KEYS.identity,
        title: formatMessage({ id: "filter-by-identity", defaultMessage: "Identity" }),
        columns: 3,
        options: identityOptions.map((option) => ({
          id: `${FILTER_KEYS.identity}${FILTER_ID_SEPARATOR}${option.value}`,
          label: option.label,
        })),
      },
      {
        id: FILTER_KEYS.education,
        title: formatMessage({ id: "filter-by-education", defaultMessage: "Education" }),
        columns: 3,
        options: educationOptions.map((option) => ({
          id: `${FILTER_KEYS.education}${FILTER_ID_SEPARATOR}${option.value}`,
          label: option.label,
        })),
      },
    ]
  }, [formatMessage, personalityOptions, interestsOptions, identityOptions, educationOptions])

  const handleUnifiedFilterToggle = (optionId) => {
    const [filterKey, value] = optionId.split(FILTER_ID_SEPARATOR)

    if (!filterKey || !value) {
      return
    }

    const isChecked = selectedRefinements[filterKey]?.includes(value)
    toggleRefinement(filterKey, value, !isChecked)
  }

  return (
    <LocalExplore
      classContext={classContext}
      filterList={filterList}
      items={filteredVideos}
      searchKeys={videosSearchKeys}
      hitsPerPage={VIDEOS_PER_PAGE}
      searchPlaceholder={formatMessage({
        id: "algolia-videos-explore-search-placeholder",
        defaultMessage: "Search by story title, job title, company, location",
      })}
      renderCard={mapVideoToCard}
      getRecencyDate={(item) => item.created_at || item.simplified_version_created_at || item.modified_at || item.published_at || item.simplified_version_modified_at}
      renderSortExtras={() => {
        return (
          <ExploreUnifiedFiltersDropdown
            heading={formatMessage({ id: "filter-videos-by", defaultMessage: "Filter Videos by" })}
            viewLabel={formatMessage({ id: "algolia-toggle-dropdown-videos", defaultMessage: "Videos" })}
            sections={filterSections}
            selectedOptionIds={selectedFilterOptionIds}
            onToggleOption={handleUnifiedFilterToggle}
            onClearAll={clearRefinements}
          />
        )
      }}
    />
  )
}

export default VideosExplore
